import FocusTrap from "focus-trap-react";
import { KalturaPlayer } from "src/lib/kaltura-player";
import { PrimaryButton } from "../button";
import { LINK_STYLES, ACTION_BUTTON } from "../plenary-download/plenary-download";
import { Pause, Play, SkipBack, SkipForward, TimeStamp } from "./clip-controls";
import { useVideoClip } from "./use-video-clip";
import { SubtitleOption } from "src/models/subtitle-option.model";

interface CreateVideoClipProps {
  source: string;
  subtitleOption: SubtitleOption;
  handleCreateClip: (videoUrl: string, subtitleUrl: string) => Promise<void>;
  handleCancel?: () => void;
}

export function CreateVideoClip({ source, subtitleOption, handleCancel, handleCreateClip }: CreateVideoClipProps) {
  const {
    entryId,
    handlePlayerLoaded,
    message,
    messageStatusClassname,
    startTime,
    startLeft,
    startRef,
    handleStartTimeChange,
    handleStartKeydown,
    handleToStart,
    endTime,
    endLeft,
    endRef,
    handleEndTimeChange,
    handleEndKeydown,
    handleToEnd,
    clipRange,
    currentTime,
    playerPlaying,
    playerPlay,
    playerPause,
    playerDuration,
    disabled,
    createClip,
  } = useVideoClip(source, subtitleOption, handleCreateClip);

  return (
    <FocusTrap>
      <div className="select-none">
        <div className="relative w-full overflow-hidden" style={{ paddingBottom: "56.25%" }}>
          <KalturaPlayer
            className="absolute top-0 left-0 w-full h-full clip-preview"
            entryId={entryId}
            autoplay={true}
            onPlayerLoaded={handlePlayerLoaded}
          />
          <div
            className={`absolute top-0 left-0 translate-x-0 w-full p-2 text-center ${messageStatusClassname} text-white duration-150 z-[2] transition-background-color ${
              message ? "translate-y-0" : "-translate-y-full"
            }`}
          >
            {message}
          </div>
        </div>
        <p className="text-2xs mt-1 mb-8 text-center">
          <strong>Hinweis:</strong> In Abhängigkeit der gewählten Untertitel-Option und Clip-Länge kann die Fertigstellung mehrere Minuten an Anspruch nehmen.
        </p>
        <div className="flex items-center mt-4">
          <TimeStamp min={0} max={endTime} time={startTime} handleTimeChange={handleStartTimeChange} />
          <div className="flex-1 mx-2 bg-gray-400">
            <div className="relative flex h-16 mx-5" ref={clipRange}>
              <div
                className="absolute flex justify-center items-center h-full bg-gray-100"
                style={{
                  left: `calc(${startLeft * 100}% - 1px)`,
                  width: `calc(${(endLeft - startLeft) * 100}% + 2px)`,
                }}
              ></div>
              <button
                type="button"
                aria-label="Set in"
                className="absolute h-full w-5 text-3xl bg-red-400 text-white"
                style={{ left: `${startLeft * 100}%`, transform: "translateX(-100%)" }}
                onKeyDown={handleStartKeydown}
                ref={startRef}
              >
                &#10093;
              </button>
              <button
                type="button"
                aria-label="Set out"
                className="absolute h-full w-5 text-3xl bg-red-400 text-white"
                style={{ left: `${endLeft * 100}%` }}
                onKeyDown={handleEndKeydown}
                ref={endRef}
              >
                &#10092;
              </button>
            </div>
          </div>
          <TimeStamp
            min={startTime}
            max={playerDuration.current}
            time={endTime}
            handleTimeChange={handleEndTimeChange}
          />
        </div>
        <div className="flex justify-center items-center gap-6 mt-2">
          <SkipBack disabled={currentTime <= startTime} handleClick={handleToStart} />
          {playerPlaying ? (
            <Pause handleClick={playerPause} />
          ) : (
            <Play disabled={endTime <= currentTime} handleClick={playerPlay} />
          )}
          <SkipForward disabled={currentTime >= endTime} handleClick={handleToEnd} />
        </div>
        <div className="flex justify-end items-center gap-4 mt-4">
          <button type="button" className={`${LINK_STYLES} py-2`} onClick={handleCancel}>
            Abbrechen
          </button>
          <PrimaryButton className={ACTION_BUTTON} disabled={disabled} onClick={createClip}>
            Schnitt anwenden
          </PrimaryButton>
        </div>
      </div>
    </FocusTrap>
  );
}
